import { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import {
    PricingConfig,
    DEFAULT_CONFIG,
    ReferralRewards,
    BoosterPack,
    TierLimit,
    ActiveSale,
    AdsgramConfig,
    XPRewardsConfig,
    DailyRewardDay,
} from "../types/config";

// Admin API base URL - defaults to same origin for production
const ADMIN_API_BASE_URL = import.meta.env.VITE_ADMIN_API_URL || "";

// Helper to safely parse JSON responses
async function safeParseJson(response: Response): Promise<any> {
    const text = await response.text();
    try {
        return JSON.parse(text);
    } catch {
        // If response is HTML (common for 404/502 errors), extract useful info
        if (text.includes("<!DOCTYPE") || text.includes("<html")) {
            throw new Error(
                `Server returned HTML instead of JSON (status ${response.status}). ` +
                `Check that VITE_ADMIN_API_URL is configured correctly.`
            );
        }
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}...`);
    }
}

export function useConfiguration() {
    const [config, setConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
    const [originalConfig, setOriginalConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [savingSection, setSavingSection] = useState<string | null>(null);
    const [savedSection, setSavedSection] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isNew, setIsNew] = useState(false);

    // Get Firebase ID token for API authentication
    const getIdToken = useCallback(async (): Promise<string | null> => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return null;
        try {
            return await user.getIdToken();
        } catch (e) {
            console.error("Failed to get ID token:", e);
            return null;
        }
    }, []);

    // Deep compare two values
    const isEqual = (a: any, b: any): boolean => {
        if (a === b) return true;
        if (a == null || b == null) return a == b;
        if (typeof a !== typeof b) return false;
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => isEqual(v, b[i]));
        }
        if (typeof a === "object") {
            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            if (keysA.length !== keysB.length) return false;
            return keysA.every((k) => isEqual(a[k], b[k]));
        }
        return false;
    };

    // Check if a section has changes
    const hasChanges = (section: string): boolean => {
        switch (section) {
            case "prices":
                return config.pro_monthly_stars !== originalConfig.pro_monthly_stars ||
                    config.pro_yearly_stars !== originalConfig.pro_yearly_stars;
            case "referrals":
                return !isEqual(config.referral_rewards, originalConfig.referral_rewards);
            case "tiers":
                return !isEqual(config.tier_limits, originalConfig.tier_limits);
            case "boosters":
                return !isEqual(config.booster_packs, originalConfig.booster_packs);
            case "sale":
                return !isEqual(config.active_sale, originalConfig.active_sale);
            case "adsgram":
                return !isEqual(config.adsgram, originalConfig.adsgram);
            case "system":
                return config.promo_codes_enabled !== originalConfig.promo_codes_enabled ||
                    config.gemini_cost_per_1m_tokens !== originalConfig.gemini_cost_per_1m_tokens ||
                    config.certificates_enabled !== originalConfig.certificates_enabled ||
                    config.leaderboard_enabled !== originalConfig.leaderboard_enabled ||
                    config.coupons_enabled !== originalConfig.coupons_enabled;
            case "gamification":
                return !isEqual(config.xp_rewards, originalConfig.xp_rewards) ||
                    !isEqual(config.daily_login_rewards, originalConfig.daily_login_rewards);
            default:
                return false;
        }
    };

    const loadConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getIdToken();
            if (!token) {
                setError("Not authenticated");
                setLoading(false);
                return;
            }

            const response = await fetch(`${ADMIN_API_BASE_URL}/admin/api/config`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await safeParseJson(response).catch(() => ({}));
                throw new Error(errorData.detail || `API error: ${response.status}`);
            }

            const data = await safeParseJson(response);

            // Map API response to PricingConfig format
            const mergedConfig: PricingConfig = {
                pro_monthly_stars: data.pro_monthly_stars ?? DEFAULT_CONFIG.pro_monthly_stars,
                pro_yearly_stars: data.pro_yearly_stars ?? DEFAULT_CONFIG.pro_yearly_stars,
                booster_packs: {},
                promo_codes_enabled: data.promo_codes_enabled ?? DEFAULT_CONFIG.promo_codes_enabled,
                active_sale: {
                    ...DEFAULT_CONFIG.active_sale,
                    ...data.active_sale,
                },
                referral_rewards: {
                    ...DEFAULT_CONFIG.referral_rewards,
                    ...data.referral_rewards,
                },
                tier_limits: {
                    free: {
                        ...DEFAULT_CONFIG.tier_limits.free,
                        ...data.tier_limits?.free,
                    },
                    pro: {
                        ...DEFAULT_CONFIG.tier_limits.pro,
                        ...data.tier_limits?.pro,
                    },
                },
                adsgram: {
                    ...DEFAULT_CONFIG.adsgram,
                    ...data.adsgram,
                },
                gemini_cost_per_1m_tokens: data.gemini_cost_per_1m_tokens ?? DEFAULT_CONFIG.gemini_cost_per_1m_tokens,
                certificates_enabled: data.certificates_enabled ?? DEFAULT_CONFIG.certificates_enabled,
                leaderboard_enabled: data.leaderboard_enabled ?? DEFAULT_CONFIG.leaderboard_enabled,
                coupons_enabled: data.coupons_enabled ?? DEFAULT_CONFIG.coupons_enabled,
                xp_rewards: {
                    ...DEFAULT_CONFIG.xp_rewards,
                    ...data.xp_rewards,
                },
                daily_login_rewards: {
                    schedule: data.daily_login_rewards?.schedule?.length
                        ? data.daily_login_rewards.schedule
                        : DEFAULT_CONFIG.daily_login_rewards.schedule,
                },
            };

            // Use DB booster packs if available, otherwise fall back to defaults
            const boosterPacks: Record<string, BoosterPack> = {};
            const sourcePacks = data.booster_packs || DEFAULT_CONFIG.booster_packs;

            Object.keys(sourcePacks).forEach(key => {
                boosterPacks[key] = {
                    ...(DEFAULT_CONFIG.booster_packs[key] || { stars_price: 0, voice_credits: 10, enabled: true, icon_type: "mic" }),
                    ...sourcePacks[key]
                };
            });
            mergedConfig.booster_packs = boosterPacks;

            setConfig(mergedConfig);
            setOriginalConfig(JSON.parse(JSON.stringify(mergedConfig)));
            setIsNew(false);
        } catch (e: any) {
            console.error("Error loading config:", e);
            setError(e.message || "Failed to load configuration");
            // Fall back to defaults
            setConfig(DEFAULT_CONFIG);
            setOriginalConfig(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
            setIsNew(true);
        } finally {
            setLoading(false);
        }
    }, [getIdToken]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    async function saveSection(sectionName: string, data: Partial<PricingConfig>) {
        setSavingSection(sectionName);
        setError(null);
        setSavedSection(null);
        try {
            const token = await getIdToken();
            if (!token) {
                setError("Not authenticated");
                setSavingSection(null);
                return;
            }

            const response = await fetch(`${ADMIN_API_BASE_URL}/admin/api/config`, {
                method: "PATCH",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                const errorData = await safeParseJson(response).catch(() => ({}));
                throw new Error(errorData.detail || `API error: ${response.status}`);
            }

            const updatedData = await safeParseJson(response);

            // Map API response back to PricingConfig format to ensure we are in sync
            const newConfig: PricingConfig = {
                ...config,
                pro_monthly_stars: updatedData.pro_monthly_stars ?? config.pro_monthly_stars,
                pro_yearly_stars: updatedData.pro_yearly_stars ?? config.pro_yearly_stars,
                promo_codes_enabled: updatedData.promo_codes_enabled ?? config.promo_codes_enabled,
                gemini_cost_per_1m_tokens: updatedData.gemini_cost_per_1m_tokens ?? config.gemini_cost_per_1m_tokens,
                certificates_enabled: updatedData.certificates_enabled ?? config.certificates_enabled,
                leaderboard_enabled: updatedData.leaderboard_enabled ?? config.leaderboard_enabled,
                coupons_enabled: updatedData.coupons_enabled ?? config.coupons_enabled,
                xp_rewards: updatedData.xp_rewards
                    ? { ...config.xp_rewards, ...updatedData.xp_rewards }
                    : config.xp_rewards,
                daily_login_rewards: updatedData.daily_login_rewards?.schedule?.length
                    ? { schedule: updatedData.daily_login_rewards.schedule }
                    : config.daily_login_rewards,
                active_sale: { ...config.active_sale, ...updatedData.active_sale },
                referral_rewards: { ...config.referral_rewards, ...updatedData.referral_rewards },
                adsgram: { ...config.adsgram, ...updatedData.adsgram },
            };

            if (updatedData.tier_limits) {
                newConfig.tier_limits = {
                    free: { ...config.tier_limits.free, ...updatedData.tier_limits.free },
                    pro: { ...config.tier_limits.pro, ...updatedData.tier_limits.pro },
                };
            }

            if (updatedData.booster_packs) {
                const newPacks: Record<string, BoosterPack> = {};
                Object.keys(updatedData.booster_packs).forEach(key => {
                    newPacks[key] = {
                        ...(config.booster_packs[key] || { stars_price: 0, voice_credits: 10, enabled: true, icon_type: "mic" }),
                        ...updatedData.booster_packs[key]
                    };
                });
                newConfig.booster_packs = newPacks;
            }

            setConfig(newConfig);
            setOriginalConfig(JSON.parse(JSON.stringify(newConfig)));
            setSavedSection(sectionName);
            setIsNew(false);
            setTimeout(() => setSavedSection(null), 3000);
        } catch (e: any) {
            console.error("Error saving config:", e);
            setError(e.message || "Failed to save configuration");
        } finally {
            setSavingSection(null);
        }
    }

    const updateConfig = <K extends keyof PricingConfig>(key: K, value: PricingConfig[K]) => {
        setConfig((prev) => ({ ...prev, [key]: value }));
    };

    const updateReferral = <K extends keyof ReferralRewards>(key: K, value: ReferralRewards[K]) => {
        setConfig((prev) => ({
            ...prev,
            referral_rewards: { ...prev.referral_rewards, [key]: value },
        }));
    };

    const updateBooster = (pack: string, field: keyof BoosterPack, value: any) => {
        setConfig((prev) => ({
            ...prev,
            booster_packs: {
                ...prev.booster_packs,
                [pack]: { ...prev.booster_packs[pack], [field]: value },
            },
        }));
    };

    const addBooster = (packId: string) => {
        setConfig((prev) => ({
            ...prev,
            booster_packs: {
                ...prev.booster_packs,
                [packId]: { stars_price: 0, voice_credits: 0, enabled: false, icon_type: "mic" },
            },
        }));
    };

    const removeBooster = (packId: string) => {
        setConfig((prev) => {
            const next = { ...prev.booster_packs };
            delete next[packId];
            return { ...prev, booster_packs: next };
        });
    };

    const updateTier = (tier: keyof PricingConfig["tier_limits"], field: keyof TierLimit, value: number | undefined) => {
        setConfig((prev) => ({
            ...prev,
            tier_limits: {
                ...prev.tier_limits,
                [tier]: { ...prev.tier_limits[tier], [field]: value },
            },
        }));
    };

    const updateSale = <K extends keyof ActiveSale>(key: K, value: ActiveSale[K]) => {
        setConfig((prev) => ({
            ...prev,
            active_sale: { ...prev.active_sale, [key]: value },
        }));
    };

    const updateAdsgram = <K extends keyof AdsgramConfig>(key: K, value: AdsgramConfig[K]) => {
        setConfig((prev) => ({
            ...prev,
            adsgram: { ...prev.adsgram, [key]: value },
        }));
    };

    const updateXPRewards = <K extends keyof XPRewardsConfig>(key: K, value: XPRewardsConfig[K]) => {
        setConfig((prev) => ({
            ...prev,
            xp_rewards: { ...prev.xp_rewards, [key]: value },
        }));
    };

    const updateDailyLoginDay = (dayIndex: number, field: keyof DailyRewardDay, value: number) => {
        setConfig((prev) => {
            const schedule = [...prev.daily_login_rewards.schedule];
            schedule[dayIndex] = { ...schedule[dayIndex], [field]: value };
            return {
                ...prev,
                daily_login_rewards: { schedule },
            };
        });
    };

    const addDailyLoginDay = () => {
        setConfig((prev) => ({
            ...prev,
            daily_login_rewards: {
                schedule: [...prev.daily_login_rewards.schedule, { text_credits: 0, voice_credits: 0 }],
            },
        }));
    };

    const removeDailyLoginDay = (dayIndex: number) => {
        setConfig((prev) => ({
            ...prev,
            daily_login_rewards: {
                schedule: prev.daily_login_rewards.schedule.filter((_, i) => i !== dayIndex),
            },
        }));
    };

    return {
        config,
        loading,
        savingSection,
        savedSection,
        error,
        isNew,
        hasChanges,
        saveSection,
        updateConfig,
        updateReferral,
        updateBooster,
        addBooster,
        removeBooster,
        updateTier,
        updateSale,
        updateAdsgram,
        updateXPRewards,
        updateDailyLoginDay,
        addDailyLoginDay,
        removeDailyLoginDay,
    };
}
